import { gruposDoFunil, nomeEtapa } from '../data/etapas';
import {
    LuArrowRightLeft,
    LuCopy,
    LuFilterX,
    LuLink,
    LuMessageSquare,
    LuNotebookPen,
    LuListPlus,
    LuSignpost,
    LuSparkles,
    LuSquareArrowOutUpRight,
    LuThermometer,
    LuTrash2,
    LuUserPlus,
} from 'react-icons/lu';
import type { ItemMenuContexto } from './MenuContexto';

/**
 * O que o menu precisa saber do negócio. É o recorte do `Deal` da PipelinePage — interface
 * própria para o componente não depender da página (o cartão do quadro e a linha da lista
 * passam o mesmo objeto).
 */
export interface NegocioDoMenu {
    id: number;
    title: string;
    statusId: number;
    tipoLeadId?: number;
    phone?: string;
    contactId: number;
    ownerId?: number;
    idOrigemOportunidade?: number;
}

/** O que o menu pode fazer com um negócio. Quem implementa é a PipelinePage; o cartão e a linha só chamam. */
export interface AcoesNegocio {
    moverEtapa: (deal: NegocioDoMenu, statusAlvo: number) => void;
    definirTemperatura: (deal: NegocioDoMenu, tipoLeadId: number) => void;
    assumir: (deal: NegocioDoMenu) => void;
    transferir: (deal: NegocioDoMenu) => void;
    abrirConversa: (deal: NegocioDoMenu) => void;
    copiarTelefone: (deal: NegocioDoMenu) => void;
    resumir: (deal: NegocioDoMenu) => void;
    criarTarefa: (deal: NegocioDoMenu) => void;
    adicionarAnotacao: (deal: NegocioDoMenu) => void;
    copiarLink: (deal: NegocioDoMenu) => void;
    abrirEmNovaAba: (deal: NegocioDoMenu) => void;
    tirarDoFunil: (deal: NegocioDoMenu) => void;
    excluir: (deal: NegocioDoMenu) => void;
    /**
     * O que esta pessoa pode ver neste negócio. O item some em vez de aparecer morto — a mesma
     * regra do menu da conversa.
     */
    permite: {
        /** `podeAssumir` da página: sem dono, qualquer um; com dono, só o administrador e fora de Ganho/Perdido. */
        assumir: boolean;
        /** Passar para outro vendedor pede a mesma régua de assumir: ninguém troca o dono de venda fechada. */
        transferir: boolean;
        /** Sem tipo de tarefa cadastrado não há como criar tarefa. */
        criarTarefa: boolean;
        /** Excluir é só do administrador da empresa (papel, não permissão de catálogo). */
        excluir: boolean;
    };
}

const ICONE = 14;

/**
 * As etapas que o menu oferece como destino, na ordem do funil. São os mesmos alvos do arrastar
 * (GRUPOS da PipelinePage) e do select da ficha: ninguém devolve um negócio para a IA, então
 * Atendimento IA (1) e IA encerrada (8) não são destino — só aparecem, desabilitadas, quando o
 * negócio está numa delas, senão a lista esconderia onde ele está.
 */
/* O miolo do funil da empresa mais os dois desfechos. Era uma lista de ids, e etapa criada
   depois não aparecia no menu — o vendedor não conseguia mover o negócio para ela daqui. */
const destinos = () => gruposDoFunil().filter(g => g.cor !== 'ia').map(g => g.alvo);

/**
 * O nome de cada etapa **na grafia do quadro** (só a primeira maiúscula), e não a do `NOME_ETAPA`,
 * que escreve "Em Qualificação": a coluna fica a dois centímetros do menu, e duas grafias do mesmo
 * nome lado a lado se notam.
 */
/* O nome vem do funil da empresa, que já é escrito na grafia do quadro. */
const ROTULO_ETAPA = new Proxy({} as Record<number, string>, {
    get: (_, chave) => nomeEtapa(Number(chave)),
});

const TEMPERATURAS: { id: number; rotulo: string }[] = [
    { id: 1, rotulo: 'Quente' },
    { id: 2, rotulo: 'Morno' },
    { id: 3, rotulo: 'Frio' },
];

/**
 * A lista única de ações do negócio — a do botão direito no cartão do quadro e na linha da Lista.
 * Uma ação, um nome, dois lugares, como no menu da conversa (`conversaMenu.tsx`).
 *
 * Quatro seções: **Negócio** (o que muda o estado), **Contato** (o que fala com a pessoa),
 * **Registro** (o que deixa rastro) e, depois de um filete, o que não tem volta fácil.
 *
 * ⚠ Mudar etapa aqui **move na hora**, igual ao arrastar, à régua da ficha e ao select ao lado dela.
 * Só Ganho e Perdido param antes: a API exige produto para ganhar, e o motivo para perder — os dois
 * diálogos são os mesmos do arrastar. A temperatura é opcional em qualquer etapa.
 */
export const itensDoNegocio = (deal: NegocioDoMenu, acoes: AcoesNegocio): ItemMenuContexto[] => {
    const naIA = deal.statusId === 1 || deal.statusId === 8;

    const etapas: ItemMenuContexto[] = [];
    if (naIA) {
        etapas.push({
            id: `etapa-${deal.statusId}`,
            rotulo: ROTULO_ETAPA[deal.statusId],
            detalhe: 'atual',
            desabilitado: true,
            onSelecionar: () => undefined,
        });
    }
    destinos().forEach(id => {
        const atual = id === deal.statusId;
        etapas.push({
            id: `etapa-${id}`,
            rotulo: ROTULO_ETAPA[id],
            detalhe: atual ? 'atual' : undefined,
            desabilitado: atual,
            onSelecionar: () => acoes.moverEtapa(deal, id),
        });
    });

    const negocio: ItemMenuContexto[] = [
        {
            id: 'etapa',
            rotulo: 'Mudar etapa',
            icone: <LuSignpost size={ICONE} />,
            onSelecionar: () => undefined,
            itens: etapas,
        },
        {
            id: 'temperatura',
            rotulo: 'Temperatura',
            icone: <LuThermometer size={ICONE} />,
            onSelecionar: () => undefined,
            itens: TEMPERATURAS.map(t => ({
                id: `temp-${t.id}`,
                rotulo: t.rotulo,
                detalhe: deal.tipoLeadId === t.id ? 'atual' : undefined,
                desabilitado: deal.tipoLeadId === t.id,
                onSelecionar: () => acoes.definirTemperatura(deal, t.id),
            })),
        },
    ];

    if (acoes.permite.assumir) {
        negocio.push({
            id: 'assumir',
            rotulo: 'Assumir',
            icone: <LuUserPlus size={ICONE} />,
            onSelecionar: () => acoes.assumir(deal),
        });
    }

    if (acoes.permite.transferir) {
        negocio.push({
            id: 'transferir',
            rotulo: 'Transferir para…',
            icone: <LuArrowRightLeft size={ICONE} />,
            onSelecionar: () => acoes.transferir(deal),
        });
    }

    /* Cadastro manual (origem 6) não tem diálogo nenhum: abrir a conversa levaria a uma tela vazia e
       resumir não teria o que resumir. Os dois somem; copiar telefone continua, se houver número. */
    const temConversa = deal.idOrigemOportunidade !== 6;
    const contato: ItemMenuContexto[] = [];

    if (temConversa) {
        contato.push({
            id: 'conversa',
            rotulo: 'Abrir a conversa',
            icone: <LuMessageSquare size={ICONE} />,
            onSelecionar: () => acoes.abrirConversa(deal),
        });
    }

    if (deal.phone?.replace(/\D/g, '')) {
        contato.push({
            id: 'copiar-telefone',
            rotulo: 'Copiar telefone',
            icone: <LuCopy size={ICONE} />,
            onSelecionar: () => acoes.copiarTelefone(deal),
        });
    }

    if (temConversa) {
        contato.push({
            id: 'resumir',
            rotulo: 'Resumir com IA',
            icone: <LuSparkles size={ICONE} />,
            onSelecionar: () => acoes.resumir(deal),
        });
    }

    const registro: ItemMenuContexto[] = [];

    if (acoes.permite.criarTarefa) {
        registro.push({
            id: 'criar-tarefa',
            rotulo: 'Criar tarefa',
            icone: <LuListPlus size={ICONE} />,
            onSelecionar: () => acoes.criarTarefa(deal),
        });
    }

    registro.push(
        {
            id: 'anotacao',
            rotulo: 'Adicionar anotação',
            icone: <LuNotebookPen size={ICONE} />,
            onSelecionar: () => acoes.adicionarAnotacao(deal),
        },
        {
            id: 'copiar-link',
            rotulo: 'Copiar link',
            icone: <LuLink size={ICONE} />,
            onSelecionar: () => acoes.copiarLink(deal),
        },
        {
            id: 'nova-aba',
            rotulo: 'Abrir em nova aba',
            icone: <LuSquareArrowOutUpRight size={ICONE} />,
            onSelecionar: () => acoes.abrirEmNovaAba(deal),
        },
    );

    /* O que não tem volta fácil, depois de um filete e sem título de seção: "Negócio" já está em cima,
       e um segundo título ali leria como se fosse outro assunto. */
    const risco: ItemMenuContexto[] = [];

    /* Fornecedor ou contato pessoal não é negócio: o contato sai do funil e as conversas ficam no Chat.
       Em Ganho não se oferece — a venda fechada continua contando no Analytics e nas metas. */
    if (deal.contactId && deal.statusId !== 3) {
        risco.push({
            id: 'funil',
            rotulo: 'Tirar do funil',
            icone: <LuFilterX size={ICONE} />,
            onSelecionar: () => acoes.tirarDoFunil(deal),
        });
    }

    /* Excluir só aparece para o administrador da empresa — e some, não aparece morto, como o resto
       do menu. Quem não pode excluir continua com "Tirar do funil", que é a saída reversível. */
    if (acoes.permite.excluir) {
        risco.push({
            id: 'excluir',
            rotulo: 'Excluir oportunidade',
            icone: <LuTrash2 size={ICONE} />,
            perigoso: true,
            onSelecionar: () => acoes.excluir(deal),
        });
    }

    /* O título vai no primeiro item visível de cada seção: seção sem item não deixa cabeçalho solto. */
    const secoes: [string | undefined, ItemMenuContexto[]][] = [
        ['Negócio', negocio],
        ['Contato', contato],
        ['Registro', registro],
        [undefined, risco],
    ];
    const saida: ItemMenuContexto[] = [];
    for (const [titulo, lista] of secoes) {
        if (!lista.length) continue;
        if (titulo) lista[0].tituloAntes = titulo;
        else lista[0].separadorAntes = true;
        saida.push(...lista);
    }

    return saida;
};
